import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Inbox, Clock, CheckCircle2, ArrowRightLeft, XCircle, ChevronRight, ChevronDown, Boxes,
} from "lucide-react";
import { cn } from "../../utils/tailwindUtils";
import {
  DashboardSection, DashboardTable, DistributionBars, SectionGrid, StatTile, TileGrid,
} from "../../components/dashboard";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { ASSET_REQUEST } from "../../constants/api";
import {
  ASSET_REQUEST_TYPES, ASSET_REQUEST_STATUSES, shortType,
} from "../../constants/assetRequest";
import { StatusBadge, fmtDate } from "../assetmanagement/AssetRequestPieces";

/* Self-contained asset-request overview (stat tiles + by-type/by-status bars +
   recent), in a collapsible card like DayEndApprovalCard. Auto-expands whenever
   the user has any OPEN request (Pending / Approved / Issued). Shared by the
   standalone admin page and the Home / AdminDashboard screens.

   The body is built from the shared dashboard kit so this reads the same as
   Grievance and Payroll. The collapsible shell around it stays local: this is
   the one dashboard block that is EMBEDDED in another screen, so it cannot use
   DashboardShell (that owns the page surface and title). */

// Bar colours as classes, not hex: DistributionBars takes a className so the
// bars respond to the theme the same way every other dashboard's do.
const STATUS_BAR = {
  Pending: "bg-amber-600",
  Approved: "bg-violet-500",
  Issued: "bg-green-600",
  Rejected: "bg-red-600",
  Returned: "bg-sky-500",
};

// Status → compact-summary pill palette.
const PILL = {
  Total: "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
  Pending: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800",
  Approved: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-300 dark:border-violet-800",
  Issued: "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800",
  Rejected: "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800",
};

const RECENT_COLUMNS = [
  {
    key: "requester",
    header: "Request",
    render: (r) => (
      <span className="block">
        <span className="block text-sm font-medium">
          {r.requester_name} · <span className="text-muted-foreground">{r.category_name}</span>
        </span>
        <span className="block text-xs text-muted-foreground">
          {r.request_no} · {r.department} · {fmtDate(r.created_at)}
        </span>
      </span>
    ),
  },
  {
    key: "status",
    header: "Status",
    className: "text-right",
    headClassName: "text-right",
    render: (r) => <StatusBadge status={r.status} />,
  },
];

const AssetDashboardSection = () => {
  const navigate = useNavigate();
  const { fetch } = useApiMethods();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(ASSET_REQUEST);
      // Backend paginates: data = { result: [...], total_count, ... }.
      if (res?.status === "success") {
        setRequests(Array.isArray(res.data) ? res.data : (res.data?.result || []));
      }
    } catch (e) {
      ToastMsg("error", e.message);
    } finally {
      setLoading(false);
    }
    // `fetch` is a new ref every render — keep it out of deps to avoid a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const count = useCallback((s) => requests.filter((r) => r.status === s).length, [requests]);

  // "Open" = anything not yet closed (Rejected/Returned).
  const openCount = useMemo(
    () => requests.filter((r) => ["Pending", "Approved", "Issued"].includes(r.status)).length,
    [requests]
  );

  // Auto-expand when the user currently has any open request; collapse otherwise.
  useEffect(() => { setExpanded(openCount > 0); }, [openCount]);

  const cards = useMemo(() => ([
    { label: "Total Requests", v: requests.length, Icon: Inbox, tone: "info" },
    { label: "Pending", v: count("Pending"), Icon: Clock, tone: "warning", status: "Pending" },
    { label: "Approved", v: count("Approved"), Icon: CheckCircle2, tone: "info", status: "Approved" },
    { label: "Issued", v: count("Issued"), Icon: ArrowRightLeft, tone: "success", status: "Issued" },
    { label: "Rejected", v: count("Rejected"), Icon: XCircle, tone: "danger", status: "Rejected" },
  ]), [requests, count]);

  // The list seeds its status chip from this state, so the tile and the screen
  // it opens agree. A tile with nothing behind it stays unclickable rather than
  // opening an empty list.
  const openList = useCallback(
    (status) => navigate("/assetmanagement/assetrequest", status ? { state: { status } } : undefined),
    [navigate],
  );

  const summaryPills = useMemo(() => ([
    ["Total", requests.length],
    ["Pending", count("Pending")],
    ["Approved", count("Approved")],
    ["Issued", count("Issued")],
    ["Rejected", count("Rejected")],
  ]), [requests, count]);

  const byType = useMemo(
    () => ASSET_REQUEST_TYPES.map((t) => ({
      key: t,
      label: shortType(t),
      count: requests.filter((r) => r.request_type === t).length,
    })),
    [requests]
  );
  const byStatus = useMemo(
    () => ASSET_REQUEST_STATUSES
      .map((s) => ({ key: s, label: s, count: count(s) }))
      .filter((d) => d.count),
    [requests, count]
  );
  const recent = useMemo(
    () => [...requests].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5),
    [requests]
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header (click to toggle) */}
      <div className="p-5 cursor-pointer" onClick={() => setExpanded((p) => !p)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Boxes className="w-5 h-5 text-sidebar-primary" />
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
                Asset Requests
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                {openCount > 0
                  ? `${openCount} open request${openCount > 1 ? "s" : ""}`
                  : "Asset request overview"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={(e) => { e.stopPropagation(); navigate("/assetmanagement/assetrequest"); }}
              className="text-xs font-medium text-sidebar-primary hover:underline flex items-center gap-1"
            >
              View all <ChevronRight className="w-3 h-3" />
            </button>
            <ChevronDown className={cn("w-4 h-4 transition-transform duration-300", expanded && "rotate-180")} />
          </div>
        </div>

        {/* Compact summary (collapsed) */}
        {!expanded && (
          <div className="mt-5 flex flex-wrap gap-2">
            {summaryPills.map(([label, value]) => (
              <span
                key={label}
                className={cn("inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium", PILL[label])}
              >
                {label} <span className="font-bold">{value}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-5 space-y-4">
          {/* Five tiles, so the lg row is overridden to match — TileGrid tops out
              at four and twMerge lets the later class win. */}
          <TileGrid className="sm:grid-cols-3 lg:grid-cols-5">
            {cards.map((c) => (
              <StatTile
                key={c.label}
                Icon={c.Icon}
                label={c.label}
                value={c.v}
                tone={c.tone}
                loading={loading && !requests.length}
                onClick={c.status
                  ? (c.v ? () => openList(c.status) : undefined)
                  : () => openList(null)}
              />
            ))}
          </TileGrid>

          <SectionGrid>
            <DashboardSection title="Requests by Type">
              <DistributionBars
                rows={byType}
                labelWidth="w-28"
                emptyText="No requests yet."
              />
            </DashboardSection>
            <DashboardSection title="Requests by Status">
              <DistributionBars
                rows={byStatus}
                labelWidth="w-28"
                colorFor={(row) => STATUS_BAR[row.label] || "bg-sidebar-primary"}
                emptyText="No requests yet."
              />
              {byStatus.length > 0 && (
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Use the tiles above to open any status in the request list.
                </p>
              )}
            </DashboardSection>
          </SectionGrid>

          <DashboardSection title="Recent Requests">
            <DashboardTable
              columns={RECENT_COLUMNS}
              rows={loading && !recent.length ? [] : recent}
              onRowClick={(r) => navigate(`/assetmanagement/assetrequest/${r.id}`)}
              minWidth="min-w-[420px]"
              empty={loading ? "Loading…" : "No requests yet."}
            />
          </DashboardSection>
        </div>
      )}
    </div>
  );
};

export default AssetDashboardSection;
